import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import DateRangePicker from '../components/DateRangePicker';
import Loader from "../components/Loader/Loader";
import apiRequester from '../utils/apiClient';

function EmailMonitoring() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [EmailId, setEmailId] = useState('');
  const [appId, setAppId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templateData, setTemplateData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [searchText, setSearchText] = useState('');
  const PageNo = [5, 10, 20, 50];

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "";

    const rawValue = String(value).trim();
    if (!rawValue) return "";

    const parsed = new Date(rawValue);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      const hours = String(parsed.getHours()).padStart(2, '0');
      const minutes = String(parsed.getMinutes()).padStart(2, '0');
      const seconds = String(parsed.getSeconds()).padStart(2, '0');
      const milliseconds = String(parsed.getMilliseconds()).padStart(3, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    return rawValue;
  };

  const handleShow = async () => {
    try {
      const trimmedEmailId = EmailId.trim();
      if (!trimmedEmailId) {
        setError('Email Id is required');
        return;
      }

      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (fromDate) params.append("FromDate", formatDate(fromDate));
      if (toDate) params.append("ToDate", formatDate(toDate));
      if (trimmedEmailId) params.append('EmailId', trimmedEmailId);
      if (appId) params.append('AppId', appId);

      const response = await apiRequester('GET', `Report/GetStatusByEmail?${params.toString()}`);

      const rawItems = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.items)
            ? response.items
            : response
              ? [response]
              : [];

      setTemplateData(rawItems.map((item) => ({
        subClientCode: item?.ES_SubClientCode ?? item?.subClientCode ?? '',
        mailFrom: item?.ES_MailFROM ?? item?.mailFrom ?? '',
        mailTo: item?.ES_MailTO ?? item?.mailTo ?? '',
        insertDate: formatDateTime(item?.ES_InsertDateTime ?? item?.insertDate ?? ''),
        cc: item?.ES_CC ?? item?.cc ?? '',
        bcc: item?.ES_BCC ?? item?.bcc ?? '',
        smtpDateTime: formatDateTime(item?.smtpDateTime ?? item?.SMTPDateTime ?? ''),
        smtpInsertTime: item?.inserttime ?? item?.smtpInsertTime ?? '',
        smtpStatus: item?.smtpStatus ?? '',
        smtpRelay: item?.smtpRelay ?? '',
        mailSubject: item?.ES_MailSubject ?? item?.mailSubject ?? '',
        failureReason: item?.failureReason ?? '',
      })));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        'Failed to fetch data'
      );
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const safePageSize = Number(itemsPerPage) > 0 ? Number(itemsPerPage) : 10;

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const filteredData = templateData.filter((item) => {
    const search = searchText.toLowerCase();

    return (
      item.subClientCode?.toLowerCase().includes(search) ||
      item.mailFrom?.toLowerCase().includes(search) ||
      item.mailTo?.toLowerCase().includes(search) ||
      item.insertDate?.toLowerCase().includes(search) ||
      item.cc?.toLowerCase().includes(search) ||
      item.bcc?.toLowerCase().includes(search) ||
      item.smtpDateTime?.toLowerCase().includes(search) ||
      item.smtpInsertTime?.toLowerCase().includes(search) ||
      item.smtpStatus?.toLowerCase().includes(search) ||
      item.smtpRelay?.toLowerCase().includes(search) ||
      item.mailSubject?.toLowerCase().includes(search) ||
      item.failureReason?.toLowerCase().includes(search)
    );
  });

  const requestSort = (key) => {
    setCurrentPage(1);
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      const aStr = aVal === null || aVal === undefined ? '' : aVal.toString();
      const bStr = bVal === null || bVal === undefined ? '' : bVal.toString();

      const aNum = parseFloat(aStr.replace(/[^0-9.\-]/g, ''));
      const bNum = parseFloat(bStr.replace(/[^0-9.\-]/g, ''));

      let cmp;
      if (!isNaN(aNum) && !isNaN(bNum)) {
        cmp = aNum - bNum;
      } else {
        cmp = aStr.toLowerCase().localeCompare(bStr.toLowerCase());
      }

      return sortConfig.direction === 'ascending' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / safePageSize));

  const paginatedData = sortedData.slice(
    (currentPage - 1) * safePageSize,
    currentPage * safePageSize
  );


  useEffect(() => {
    setCurrentPage(1);
  }, [templateData, searchText, itemsPerPage]);

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";

    const str = value.toString();

    // Escape quotes by doubling them
    const escaped = str.replace(/"/g, '""');

    // Wrap if contains special characters
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
      return `"${escaped}"`;
    }

    return escaped;
  };

  const downloadCSV = () => {
    const data = filteredData.length ? filteredData : templateData;

    if (!data.length) {
      alert("No data available to download");
      return;
    }

    const headers = [
      "Sub Client Code",
      "Mail From",
      "Mail To",
      "Insert Date",
      "CC",
      "BCC",
      "SMTP Date Time",
      "SMTP Status",
      "SMTP Relay",
      "Mail Subject",
      "Failure Reason"
    ];

    const rows = data.map(item => [
      escapeCSV(item.subClientCode),
      escapeCSV(item.mailFrom),
      escapeCSV(item.mailTo),
      escapeCSV(item.insertDate),
      escapeCSV(item.cc),
      escapeCSV(item.bcc),
      escapeCSV(item.smtpDateTime),
      escapeCSV(item.smtpStatus),
      escapeCSV(item.smtpRelay),
      escapeCSV(item.mailSubject),
      escapeCSV(item.failureReason)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "MobileReport.csv");

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen">
        <Sidebar />

        <div className="flex-1">
          <Topbar />

          {loading && <Loader />}

          <main className="p-6 md:p-8">
            <div className="rounded-lg border border-slate-200 bg-white/95 px-4 py-4 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm">
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex min-w-[220px] flex-1 flex-col">
                  <span className="text-xs font-medium text-slate-600">
                    Date Range
                  </span>

                  <div className="mt-1">
                    <DateRangePicker
                      value={{ fromDate, toDate }}
                      onChange={({ fromDate: f, toDate: t }) => {
                        setFromDate(f);
                        setToDate(t);
                      }}
                      placeholder="DD-MM-YYYY ~ DD-MM-YYYY"
                      displayFormat="DD-MM-YYYY"
                      classNames="text-sm w-full border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                      showFooter={true}
                      configs={{ footer: { cancel: 'Cancel', apply: 'Apply' } }}
                    />
                  </div>
                </label>

                <label className="min-w-[180px] flex-1">
                  <span className="text-xs font-medium text-slate-600">Email Id</span>
                  <input
                    type="text"
                    value={EmailId}
                    onChange={(e) => setEmailId(e.target.value)}
                    placeholder="Enter Email Id"
                    required
                    className="mt-1 w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>

                <label className="min-w-[140px] flex-1">
                  <span className="text-xs font-medium text-slate-600">App Id</span>
                  <input
                    type="text"
                    value={appId}
                    onChange={(e) => setAppId(e.target.value)}
                    placeholder="Enter App Id"
                    className="mt-1 w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>

                <button
                  type="button"
                  onClick={handleShow}
                  disabled={loading}
                  className="rounded bg-slate-700 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Show'}
                </button>
                <button
                  type="button"
                  className="rounded bg-slate-700 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                  onClick={() => {
                    setFromDate(new Date());
                    setToDate(new Date());
                    setEmailId('');
                    setAppId('');
                    window.location.reload();
                  }}
                >
                  Reset
                </button>
              </div>

              {error && (
                <div className="mt-4 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>
            {paginatedData.length > 0 && (
              <div className="border border-slate-200 bg-white/95 px-2 sm:px-4 py-4 sm:py-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm rounded-lg">

                <div className="mb-3 flex items-center gap-1 justify-between">
                  <div className="flex items-center gap-2">
                    <select
                      value={itemsPerPage}
                      onChange={handlePageSizeChange}
                      className="w-[80px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm rounded"
                    >
                      {PageNo.map((pageNo) => (
                        <option key={pageNo} value={pageNo}>
                          {pageNo}
                        </option>
                      ))}
                    </select>

                    <span className="text-sm text-slate-600">
                      TOTAL RECORD : {templateData.length}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder="Search Here"
                    className="text-sm w-150 border border-slate-200 bg-slate-50 px-3 py-2 rounded focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </div>
                <div className="overflow-auto max-h-[300px] mt-3">

                  <table className="w-full overflow-auto min-w-[800px] table-auto border border-slate-200 bg-white text-xs">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="border px-1 py-2">S.No</th>

                        <th
                          onClick={() => requestSort("subClientCode")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Sub Client Code
                          {sortConfig.key === "subClientCode"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("mailFrom")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Mail From
                          {sortConfig.key === "mailFrom"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("mailTo")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Mail To
                          {sortConfig.key === "mailTo"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("insertDate")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Insert Date
                          {sortConfig.key === "insertDate"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("cc")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          CC
                          {sortConfig.key === "cc"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("bcc")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          BCC
                          {sortConfig.key === "bcc"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("smtpDateTime")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          SMTP Date Time
                          {sortConfig.key === "smtpDateTime"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("smtpStatus")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          SMTP Status
                          {sortConfig.key === "smtpStatus"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("smtpRelay")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          SMTP Relay
                          {sortConfig.key === "smtpRelay"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("mailSubject")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Mail Subject
                          {sortConfig.key === "mailSubject"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>

                        <th
                          onClick={() => requestSort("failureReason")}
                          className="border px-1 py-2 cursor-pointer select-none"
                        >
                          Failure Reason
                          {sortConfig.key === "failureReason"
                            ? sortConfig.direction === "ascending"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </th>
                      </tr>
                    </thead>

                    <tbody key={currentPage}>
                      {paginatedData.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="border px-1 py-1 text-center">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>

                          <td className="border px-1 py-1">{item.subClientCode}</td>
                          <td className="border px-1 py-1">{item.mailFrom}</td>
                          <td className="border px-1 py-1">{item.mailTo}</td>
                          <td className="border px-1 py-1">{item.insertDate}</td>
                          <td className="border px-1 py-1">{item.cc}</td>
                          <td className="border px-1 py-1">{item.bcc}</td>
                          <td className="border px-1 py-1">{item.smtpDateTime}</td>
                          <td className="border px-1 py-1">{item.smtpStatus}</td>
                          <td className="border px-1 py-1">{item.smtpRelay}</td>
                          <td className="border px-1 py-1">{item.mailSubject}</td>
                          <td className="border px-1 py-1">{item.failureReason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>


                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                  {/* Simple Pagination */}
                  <div className="flex items-center gap-3 ">

                    {/* Prev */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      ←
                    </button>

                    {/* Current Page */}
                    <span className="px-3 py-1 border rounded bg-cyan-500 text-white">
                      {currentPage}
                    </span>

                    {/* Next */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      →
                    </button>

                    <span className="text-sm text-slate-600">
                      {currentPage} of {totalPages}
                    </span>

                  </div>
                  <button
                    onClick={downloadCSV}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                  >
                    Download CSV
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default EmailMonitoring;
